import { useMemo } from 'react';
import type { AdminLoginEvent } from '@wlb/shared/api/admin/adminTypes';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { DataTable, type DataTableColumn } from '../../components/ui/DataTable';
import { DetailList, type DetailRow } from '../../components/ui/DetailList';
import { StatusPill } from '../../components/ui/StatusPill';
import { formatCount, formatDateTime } from '../../utils/format';
import { useUserDetail } from './userForm';
import '../../components/ui/ui.css';
import '../../pages/pages.css';

type LoginRow = AdminLoginEvent & { key: string };

/** A failed sign-in is what makes this list a lockout trail rather than a log. */
const isFailure = (result: string): boolean =>
  /fail|invalid|denied|lock/i.test(result.trim());

/** Sign-in history: the "login info" sub-module of a member record. */
export function UserLoginsTab() {
  const { t } = useTranslation('admin');
  const { user } = useUserDetail();

  const summaryRows = useMemo<readonly DetailRow[]>(
    () => [
      {
        label: t('users.details.fields.lastLogin'),
        value: formatDateTime(user.lastLoginOn),
      },
      { label: t('users.details.fields.lastLoginIp'), value: user.lastLoginIp },
      {
        label: t('users.details.fields.totalVisits'),
        value: formatCount(user.totalVisits),
      },
    ],
    [t, user],
  );

  // `loginAt` repeats across rows on the same second, so the index keeps keys
  // unique.
  const rows = useMemo<readonly LoginRow[]>(
    () =>
      user.loginHistory.map((event, index) => ({
        ...event,
        key: `${index}-${event.loginAt}`,
      })),
    [user.loginHistory],
  );

  const columns = useMemo<readonly DataTableColumn<LoginRow>[]>(
    () => [
      {
        key: 'signedIn',
        header: t('users.details.loginHistory.columns.signedIn'),
        render: row => formatDateTime(row.loginAt) || t('common.none'),
      },
      {
        key: 'signedOut',
        header: t('users.details.loginHistory.columns.signedOut'),
        render: row => formatDateTime(row.loggedOutAt) || t('common.none'),
      },
      {
        key: 'minutes',
        header: t('users.details.loginHistory.columns.minutes'),
        numeric: true,
        render: row => formatCount(row.minutes),
      },
      {
        key: 'ipAddress',
        header: t('users.details.loginHistory.columns.ipAddress'),
        render: row => (
          <span className="ui-table__mono">{row.ipAddress || t('common.none')}</span>
        ),
      },
      {
        key: 'device',
        header: t('users.details.loginHistory.columns.device'),
        render: row => (
          <span className="ui-table__truncate">{row.userAgent || t('common.none')}</span>
        ),
      },
      {
        key: 'result',
        header: t('users.details.loginHistory.columns.result'),
        render: row =>
          row.result ? (
            <StatusPill
              label={row.result}
              tone={isFailure(row.result) ? 'danger' : 'success'}
            />
          ) : (
            t('common.none')
          ),
      },
    ],
    [t],
  );

  return (
    <div className="page__section">
      <section className="page__panel" aria-labelledby="user-login-summary-heading">
        <h2 className="ui-section-title" id="user-login-summary-heading">
          {t('users.details.sections.lastSignIn')}
        </h2>
        <DetailList rows={summaryRows} />
      </section>

      <DataTable
        caption={t('users.details.sections.loginHistory')}
        columns={columns}
        rows={rows}
        rowKey={row => row.key}
        // Arrives with the record, so there is nothing left to wait for here.
        status="ready"
        emptyTitle={t('users.details.loginHistory.emptyTitle')}
        emptyMessage={t('users.details.loginHistory.emptyMessage')}
      />
    </div>
  );
}
